import request from "../utils/request";

// ─── 类型定义 ─────────────────────────────────────────────────────────────────

export type HealthStatus = "healthy" | "critical";
export type IntervalMode = "minutes" | "hours" | "days";

export interface HealthTarget {
  id: string;
  code: string;
  name: string;
  endpoint: string;
  method: string;
  params: string;
  enabled: boolean;
  intervalMode: IntervalMode;
  intervalValue: number;
  status: HealthStatus;
  failCount: number;
  lastError?: string | null;
  /** 北京时间 yyyy-MM-dd HH:mm:ss；从未检测为 null */
  lastCheckedAt?: string | null;
}

export interface HealthRecord {
  id: string;
  targetCode: string;
  name?: string | null;
  endpoint: string;
  status: HealthStatus;
  responseTimeMs: number;
  error?: string | null;
  /** 北京时间 yyyy-MM-dd HH:mm:ss */
  checkedAt: string;
}

export interface HealthRecordsResult {
  data: HealthRecord[];
  total: number;
  page: number;
  pageSize: number;
  healthyCount: number;
  criticalCount: number;
}

export interface HealthRecipient {
  id: string;
  email: string;
}

// ─── API 方法 ─────────────────────────────────────────────────────────────────

const BASE = "/project/healthmonitor";

export async function getTargets(): Promise<HealthTarget[]> {
  const res = await request.get<HealthTarget[]>(`${BASE}/targets`);
  return res.data ?? [];
}

export async function toggleTarget(code: string, enabled: boolean): Promise<HealthTarget> {
  const res = await request.put<HealthTarget>(`${BASE}/targets/${code}/toggle`, { enabled });
  return res.data;
}

export async function updateInterval(
  code: string,
  mode: IntervalMode,
  value: number,
): Promise<HealthTarget> {
  const res = await request.put<HealthTarget>(`${BASE}/targets/${code}/interval`, { mode, value });
  return res.data;
}

export async function getRecords(
  targetCode = "all",
  page = 1,
  pageSize = 50,
): Promise<HealthRecordsResult> {
  const res = await request.get<HealthRecordsResult>(`${BASE}/records`, {
    params: { targetCode, page: String(page), pageSize: String(pageSize) },
  });
  return res.data ?? { data: [], total: 0, page, pageSize, healthyCount: 0, criticalCount: 0 };
}

export async function getRecipients(): Promise<HealthRecipient[]> {
  const res = await request.get<HealthRecipient[]>(`${BASE}/recipients`);
  return res.data ?? [];
}

export async function addRecipient(email: string): Promise<HealthRecipient> {
  const res = await request.post<HealthRecipient>(`${BASE}/recipients`, { email });
  return res.data;
}

export async function removeRecipient(id: string): Promise<void> {
  await request.delete(`${BASE}/recipients/${id}`);
}
